/**
 * The in-flight `ESC` board's whole life in a flight route (G38): what it is
 * looking at, and what each of its four rows does.
 *
 * A factory rather than five functions inline in `routes/Fly.tsx`, for
 * `map-session.ts`'s reason — it is a self-contained piece of state with one
 * job, and the route runs to the file-size gate's ceiling. Splitting it out is
 * also what keeps the *decisions* readable: everything below is a rule about
 * when a sortie may be abandoned, and none of it is about JSX.
 *
 * The open/shut flag and the ever-opened latch live here rather than as signals
 * in the route, and that is `map-session.ts`'s arrangement copied for its own
 * reason. `flyChrome` is a plain function called inside `Fly`'s JSX, so a signal
 * read while building its props re-runs it and **re-creates everything it
 * returns**. With the latch inside the component, every `ESC` press threw the
 * overlay away and built a new one — decoding the shell fonts and standing up a
 * fresh `ScreenRuntime` each time, with the lazy mount defeated. Held on a
 * session object, whose identity never changes, the component survives.
 *
 * Found by screenshot, and by asking why the element had *vanished* rather than
 * gone `--hidden`. No test could see it: the board looked and behaved correctly
 * either way.
 */

import { createSignal } from "solid-js";
import type { CombatHud } from "./combat-hud.ts";
import type { FlightHud } from "./hud-snapshot.ts";
import type { MissionHud } from "./mission-hud.ts";
import type { FlightScene } from "./flight-scene-api.ts";
import { timeCompressionBlocked } from "./time-compression.ts";
import type { EscMenuSituation } from "../shell/model/esc-menu-dialog.ts";

export interface EscMenuSessionDeps {
  readonly hud: () => FlightHud;
  readonly combat: () => CombatHud;
  readonly missionHud: () => MissionHud | undefined;
  /** PREFERENCES → FLYING → BAIL OUT, as stored. */
  readonly bailEnabled: () => boolean;
  readonly scene: () => FlightScene | undefined;
  /** Shuts the board *through the route's own command*, so the sim restarts with it. */
  readonly close: () => void;
  /** A full reload, which re-seeds the identical sortie because the URL is the mission. */
  readonly reload: () => void;
}

export interface EscMenuSession {
  readonly open: () => boolean;
  readonly setOpen: (open: boolean) => void;
  /** True once `ESC` has been pressed at all — what mounts the overlay, on `MapSession.everOpened`'s rule. */
  readonly everOpened: () => boolean;
  readonly situation: () => EscMenuSituation;
  readonly onContinue: () => void;
  readonly onReturnToBase: () => void;
  readonly onRestart: () => void;
  readonly onBailOut: () => void;
}

export function createEscMenuSession(deps: EscMenuSessionDeps): EscMenuSession {
  const [open, setOpenSignal] = createSignal(false);
  const [everOpened, setEverOpened] = createSignal(false);
  const setOpen = (next: boolean): void => {
    if (next) setEverOpened(true);
    setOpenSignal(next);
  };

  /**
   * Rebuilt on every read, so the SCORE ticks under the buttons and a guard can
   * arrive — an enemy closes — while the player is still reading the board.
   */
  function situation(): EscMenuSituation {
    const contact = deps.combat();
    return {
      // `time-compression.ts`'s own gate rather than a second reading of "an
      // enemy is near". Both features refuse for the same reason and must
      // refuse at the same range, and that range is the AI's own
      // `aiDisengageRangeM` rather than a number either feature invented.
      inCombat: timeCompressionBlocked(contact.enemy?.rangeM ?? null),
      airframe: contact.airframe,
      // Always false, and that is a finding rather than a stub. `RB.EXE`'s
      // `YOU'VE BEEN CAPTURED BY THE ENEMY!` is **unreachable in this port**,
      // for the same structural reason its `YOUR MISSION IS OVER.` board is: a
      // captured pilot's sortie has already ended, the run has frozen itself and
      // the debriefing is up, so there is no flight left for `ESC` to interrupt.
      // The predicate is wired and tested in `esc-menu-dialog.ts` so the string
      // is not lost, and it starts firing the day a lane gives the port a
      // per-tick "which side of the lines am I over" signal — `MissionStats`
      // only settles `overFriendlyTerritory` at the end of a sortie.
      captured: false,
      bailEnabled: deps.bailEnabled(),
      bailAvailable: deps.hud().bailAvailable,
      score: deps.missionHud()?.score,
    };
  }

  return {
    open,
    setOpen,
    everOpened,
    situation,
    onContinue: deps.close,
    /**
     * RETURN TO BASE. Shuts the board first so the sim is not left paused under
     * the debriefing, then ends the sortie through the scene's one exit
     * (`mission-report.ts`'s `endSortie`), which latches what `silentFlight`
     * reads — a debriefing raised any other way has the engine droning behind it.
     */
    onReturnToBase: () => {
      deps.close();
      deps.scene()?.endSortie();
    },
    /** RESTART MISSION. The URL *is* the mission (`mission-launch.ts`), so a reload flies the identical sortie again. */
    onRestart: () => {
      deps.close();
      deps.reload();
    },
    /**
     * BAIL OUT. The same engine command the held `H` fires — one implementation,
     * two entry points. No second confirmation: the board is already a
     * deliberate, paused context, which is exactly what `bail-key.ts` says the
     * 1.5 s hold exists to provide in a cockpit that does not stop.
     */
    onBailOut: () => {
      deps.close();
      deps.scene()?.bailOut();
    },
  };
}
